import React, {
  useEffect, useMemo, useState, useRef,
} from "react";

import {
  Button, Flex, Input, Select,
} from "antd";
import { toast } from "react-toastify";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { getMlQcRulesSpecific } from "../../../../actions/qc";
import RuleDeleteConfirmationSpecific from "./RuleDeleteConfirmationSpecific";
import CustomerRulesTable from "./CustomerRulesTable";
import EditCustomerRulesModal from "./EditCustomerRulesModal";
import { getSearchOptions } from "../../../../actions/batchReports";
import CustomerRulesDetailsModal from "./CustomerRulesDetailsModal";
import QcBulkUploadModal from "../QcBulkUploadModal";
import QcRuleTestModal from "../QcRuleTestModal";
import { qcCustomerRuleScopes } from "../utils";
import "./CustomerRules.css";

const defaultCustomerOption = { value: "", label: "All Customers" };

export default function CustomerRules() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [mlQcRules, setMlQcRules] = useState([]);
  const [info, setInfo] = useState({ valid_match_scopes: [], valid_scopes: [] });
  const [ruleData, setRuleData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteFromTable, setDeleteFromTable] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerList, setCustomerList] = useState([defaultCustomerOption]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const abortControllerRef = useRef(null);

  const isMounted = useRef(true);

  const [selectedScope, setSelectedScope] = useState("");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const scopeOptions = useMemo(() => qcCustomerRuleScopes.reduce((acc, scope) => {
    acc.push({
      label: scope.label,
      value: scope.value,
    });
    return acc;
  }, [{
    label: "All Scopes",
    value: "",
  }]), []);

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  // Pagination state
  const defaultPageSize = 50;
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: 0,
    current: 1,
    pageSizeOptions: ["10", "25", "50", "100"],
  });

  /**
 * Fetch customer list for dropdown
 */
  const fetchCustomerList = async () => {
    setCustomerLoading(true);
    const response = await getSearchOptions();
    if (!isMounted.current) return;
    let result = [defaultCustomerOption];
    if (response && response.success) {
      result = response.list.map((item) => ({
        value: item.customer_id,
        label: `${item.company} (${item.customer_id})`,
      }));
      setCustomerList(result);
    } else {
      setCustomerList(result);
      toast.info(response.message);
    }
    setCustomerLoading(false);
  };

  /**
   * Fetch qc rules list
   * @param {string} search search text
   * @param {string} _id id
   * @param {string} customer_id customer id
   * @param {string} scope_type "" | "sample" | "generic"
   * @param {boolean} preservePage whether to preserve current page
   */
  const fetchQcRules = async (search = "", _id = "", customer_id = "", scope_type = "", preservePage = false) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const params = {
      search, _id, customer_id, scope_type,
    };
    setLoading(true);

    const response = await getMlQcRulesSpecific(params, abortController.signal);
    if (!isMounted.current) return;
    if (response.canceled) return;
    if (response.success) {
      // Update pagination config with actual data length for client-side pagination
      setPaginationConfig((prevState) => ({
        ...prevState,
        total: response.result?.length || 0,
        current: preservePage ? prevState.current : 1,
      }));

      setMlQcRules(response.result);
      const { valid_match_scopes, valid_scopes } = response.info;
      setInfo({ valid_match_scopes, valid_scopes });
      // if api call completed and abort controller has ref
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    } else {
      setMlQcRules([]);
      setInfo({ valid_match_scopes: [], valid_scopes: [] });
      // don't show error message if request is intentionally aborted
      if (!abortController?.signal?.aborted) {
        toast.error(response.message || "Something went wrong!");
      }
    }
    setLoading(false);
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  /**
   * Initial data fetching
   */
  useEffect(() => {
    fetchCustomerList();
    fetchQcRules();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Handles row click for table
   * @param {*} data row data
   */
  const onRowClick = (data) => {
    setRuleData(data);
    setShowDetailsModal(true);
  };

  /**
   * Closing all modals
   */
  const onCloseModals = () => {
    setDeleteConfirmation(false);
    setShowEditModal(false);
    setShowDetailsModal(false);
    setRuleData(null);
  };

  /**
   * Showing edit modal
   * @param {*} record row data
   */
  const onEdit = (record) => {
    setRuleData(record);
    setShowEditModal(true);
  };

  /**
   * Showing delete modal
   * @param {*} record row data
   */
  const onDelete = (record) => {
    setDeleteFromTable(true); // If deleted using Delete button in Table
    setRuleData(record);
    setDeleteConfirmation(true);
  };

  const onTest = (record) => {
    setRuleData(record);
    setShowTestModal(true);
  };

  const onCloseTestModal = () => {
    setShowTestModal(false);
    setRuleData(null);
  };

  /**
   * Refetch with current page preservation
   */
  const refetchWithCurrentPage = () => {
    fetchQcRules(searchText, "", selectedCustomer, selectedScope, true);
  };

  /**
   * Delete confirmation modal confirm click
   */
  const onDeleteConfirmation = () => {
    onCloseModals();
    refetchWithCurrentPage();
  };

  /**
   * delete confirmation modal cancel click
   */
  const onDeleteConfirmationCancel = () => {
    if (deleteFromTable) {
      setRuleData(null);
      setDeleteFromTable(false);
    }
    setDeleteConfirmation(false);
  };

  /**
   * on change event handler for search bar
   * @param {string} value search text
   */
  const handleOnSearchChange = (value) => {
    if (searchText.trim() !== "" && value === "") {
      // Remove duplicate setPaginationConfig call - let fetchQcRules handle it
      fetchQcRules(value, "", selectedCustomer, selectedScope);
    }
    setSearchText(value);
  };

  /**
   * on search handler for search bar
   * @param {string} value search text
   */
  const handleSearchText = async (value) => {
    const trimmedValue = (value || "").trim();
    // Remove duplicate setPaginationConfig call - let fetchQcRules handle it
    await fetchQcRules(trimmedValue, "", selectedCustomer, selectedScope);
  };

  const dataSource = useMemo(() => ((mlQcRules && mlQcRules.length) ? mlQcRules : []), [mlQcRules]);

  const handleScopeChange = (v) => {
    setSelectedScope(v);
    fetchQcRules(searchText, "", selectedCustomer, v);
  };

  return (
    <>
      <div className="CustomerRules_Container">
        <header>
          <Flex align="center" gap={16}>
            <Input.Search
              className="CustomerRules_Search"
              placeholder="Search by Regular Tests/Specs"
              value={searchText}
              onChange={(e) => handleOnSearchChange(e.target.value)}
              onSearch={handleSearchText}
              allowClear
            />
            <div className="field-container">
              <Select
                allowClear
                showSearch
                className="CustomerRules_Customer"
                popupClassName="CustomerRules_Customer__Menu"
                loading={customerLoading}
                options={customerList}
                defaultValue={customerList[0]?.value}
                value={selectedCustomer}
                onSelect={(v) => {
                  setSelectedCustomer(v);
                  setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  fetchQcRules(searchText, "", v, selectedScope);
                }}
                onClear={() => {
                  setSelectedCustomer({ value: "", label: "All Customers" });
                  setPaginationConfig((prev) => ({ ...prev, current: 1 }));
                  fetchQcRules(searchText, "", "", selectedScope);
                }}
                filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
                listHeight={400}
              />
            </div>
            <div className="field-container">
              <Select
                allowClear
                className="CustomerRange_ScopeSelect"
                options={scopeOptions}
                value={selectedScope}
                onSelect={(v) => handleScopeChange(v)}
                onClear={() => handleScopeChange("")}
                listHeight={400}
              />
            </div>
          </Flex>
          <Flex align="center" gap={16}>
            <Button
              type="primary"
              htmlType="button"
              onClick={() => setShowBulkUploadModal(true)}
            >
              <UploadOutlined />
              <span>Upload</span>
            </Button>
            <Button
              type="primary"
              htmlType="button"
              onClick={() => setShowEditModal(true)}
              disabled={loading}
            >
              <PlusOutlined />
              <span>New Rule</span>
            </Button>
          </Flex>
        </header>
        <div className="CustomerRules_TableContainer">
          <CustomerRulesTable
            dataSource={dataSource}
            loading={loading}
            onRowClick={onRowClick}
            onEdit={onEdit}
            onDelete={onDelete}
            onTest={onTest}
            pagination={{ ...paginationConfig, onChange: handlePageChange }}
          />
        </div>
      </div>
      {showEditModal && (
        <EditCustomerRulesModal
          open={showEditModal}
          onCancel={onCloseModals}
          editData={ruleData}
          refetch={refetchWithCurrentPage}
          customerList={customerList}
          info={info}
        />
      )}
      {showDetailsModal && (
        <CustomerRulesDetailsModal
          open={showDetailsModal}
          ruleData={ruleData}
          onCancel={onCloseModals}
          refetch={refetchWithCurrentPage}
          onDelete={() => setDeleteConfirmation(true)}
        />
      )}
      {deleteConfirmation && (
        <RuleDeleteConfirmationSpecific
          data={ruleData}
          onConfirm={onDeleteConfirmation}
          onCancel={onDeleteConfirmationCancel}
          objKey="_id"
        />
      )}
      {showBulkUploadModal && (
        <QcBulkUploadModal
          type="rules"
          open={showBulkUploadModal}
          onCancel={({ isUploadSuccessfull }) => {
            setShowBulkUploadModal(false);
            if (isUploadSuccessfull) {
              refetchWithCurrentPage();
            }
          }}
          title="Bulk Upload: Customer Rules"
        />
      )}
      {showTestModal && (
        <QcRuleTestModal
          open={showTestModal}
          onCancel={onCloseTestModal}
          ruleData={ruleData}
          title="Test Customer Rule"
        />
      )}
    </>
  );
}
